import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { PenTool, Trash2, Save, X } from 'lucide-react';
import SignaturePad from 'signature_pad';

const SignatureModal = ({ show, onClose, onSaved }) => {
    const [signatureName, setSignatureName] = useState('');
    const [currentSignatureUrl, setCurrentSignatureUrl] = useState(null);
    const [signatureExists, setSignatureExists] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [nameError, setNameError] = useState(null);

    const canvasRef = useRef(null);
    const signaturePadRef = useRef(null);

    // Load signature info when modal opens
    useEffect(() => {
        if (show) {
            loadSignatureInfo();
        }

        return () => {
            if (signaturePadRef.current) {
                signaturePadRef.current.off();
                signaturePadRef.current = null;
            }
        };
    }, [show]);

    // Initialize signature pad after loading is done and canvas is in DOM
    useEffect(() => {
        if (show && !isLoading && canvasRef.current && !signaturePadRef.current) {
            signaturePadRef.current = new SignaturePad(canvasRef.current, {
                backgroundColor: 'rgba(255, 255, 255, 0)',
                penColor: 'rgb(0, 0, 0)'
            });
        }
    }, [show, isLoading]);

    // Load current signature info
    const loadSignatureInfo = async () => {
        setIsLoading(true);
        try {
            const response = await fetch('/index.php/contracts/getSignatureInfo');
            if (response.ok) {
                const data = await response.json();
                setSignatureName(data.name || '');
                setSignatureExists(data.signatureExists);
                setCurrentSignatureUrl(data.signatureUrl);
            }
        } catch (err) {
            console.error('Error loading signature info:', err);
        } finally {
            setIsLoading(false);
        }
    };

    // Clear signature pad
    const handleClear = () => {
        if (signaturePadRef.current) {
            signaturePadRef.current.clear();
        }
    };

    // Save signature
    const handleSave = async () => {
        // Validate name
        if (!signatureName.trim()) {
            setNameError('Vor- und Nachnamen angeben!');
            return;
        }
        setNameError(null);

        setIsSaving(true);
        setError(null);

        try {
            let signData = '';
            let checkSum = 0;

            // Get signature data if not empty
            if (signaturePadRef.current && !signaturePadRef.current.isEmpty()) {
                signData = signaturePadRef.current.toDataURL('image/png');
                checkSum = 1;
            }

            // Replace + with [plus] for URL encoding
            const encodedSign = signData.replace(/\+/g, '[plus]');

            const response = await fetch('/index.php/contracts/signUpdate', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: `name=${encodeURIComponent(signatureName)}&sign=${encodeURIComponent(encodedSign)}&checkSum=${checkSum}`
            });

            if (response.ok) {
                if (onSaved) {
                    onSaved();
                }
                // Reload signature info to show new signature
                await loadSignatureInfo();
                // Clear the canvas
                handleClear();
            } else {
                setError('Fehler beim Speichern der Unterschrift');
            }
        } catch (err) {
            console.error('Error saving signature:', err);
            setError('Fehler beim Speichern der Unterschrift');
        } finally {
            setIsSaving(false);
        }
    };

    // Handle close
    const handleClose = () => {
        setError(null);
        setNameError(null);
        handleClear();
        onClose();
    };

    // Styles
    const labelStyle = {
        display: 'block',
        marginBottom: '0.5rem',
        fontWeight: '500',
        color: 'var(--text-primary, #2c3e50)',
        fontSize: '0.875rem'
    };

    const inputStyle = {
        width: '100%',
        padding: '0.625rem 1rem',
        border: '1px solid var(--border-medium, #cbd5e1)',
        borderRadius: '8px',
        fontSize: '0.875rem',
        color: 'var(--text-primary, #2c3e50)',
        background: 'var(--bg-card, #ffffff)'
    };

    const canvasContainerStyle = {
        border: '1px solid var(--border-medium, #cbd5e1)',
        borderRadius: '8px',
        background: 'var(--bg-card, #ffffff)',
        padding: '0.5rem',
        marginBottom: '0.5rem'
    };

    const previewContainerStyle = {
        padding: '1rem',
        background: 'var(--bg-secondary, #f5f7f9)',
        borderRadius: '8px',
        marginTop: '1rem'
    };

    return (
        <Modal show={show} onHide={handleClose} centered size="lg">
            <Modal.Header closeButton style={{ borderBottom: '1px solid var(--border-light, #e2e8f0)' }}>
                <Modal.Title style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    fontSize: '1.125rem',
                    fontWeight: 600,
                    color: 'var(--text-primary, #2c3e50)'
                }}>
                    <PenTool size={24} style={{ color: 'var(--accent-slate, #5b7c8d)' }} />
                    Eigene Unterschrift für Verträge
                </Modal.Title>
            </Modal.Header>

            <Modal.Body style={{ padding: '1.5rem' }}>
                {isLoading ? (
                    <div className="d-flex justify-content-center align-items-center py-4">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Lädt...</span>
                        </div>
                    </div>
                ) : (
                    <>
                        {error && (
                            <div className="alert alert-danger mb-3" role="alert">
                                {error}
                            </div>
                        )}

                        {/* Name Input */}
                        <div className="mb-3">
                            <label style={labelStyle} htmlFor="signatureName">
                                Vor- und Nachname: <span style={{ color: '#ef4444' }}>*</span>
                            </label>
                            <input
                                type="text"
                                id="signatureName"
                                style={{
                                    ...inputStyle,
                                    borderColor: nameError ? '#ef4444' : 'var(--border-medium, #cbd5e1)'
                                }}
                                value={signatureName}
                                onChange={(e) => {
                                    setSignatureName(e.target.value);
                                    if (nameError) setNameError(null);
                                }}
                                placeholder="Max Mustermann"
                            />
                            {nameError && (
                                <div style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                                    {nameError}
                                </div>
                            )}
                        </div>

                        {/* Signature Pad */}
                        <div className="mb-3">
                            <label style={labelStyle}>Unterschrift:</label>
                            <div style={canvasContainerStyle}>
                                <canvas
                                    ref={canvasRef}
                                    id="signature-pad-react"
                                    width={467}
                                    height={100}
                                    style={{
                                        display: 'block',
                                        cursor: 'crosshair',
                                        touchAction: 'none'
                                    }}
                                />
                            </div>
                            <button
                                type="button"
                                className="btn btn-sm"
                                onClick={handleClear}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '0.375rem',
                                    padding: '0.375rem 0.75rem',
                                    border: '1px solid var(--border-medium, #cbd5e1)',
                                    borderRadius: '6px',
                                    background: 'var(--bg-card, #ffffff)',
                                    color: 'var(--text-secondary, #64748b)',
                                    fontSize: '0.8125rem'
                                }}
                            >
                                <Trash2 size={14} />
                                Löschen
                            </button>
                        </div>

                        {/* Current Signature Preview */}
                        <div style={previewContainerStyle}>
                            <label style={{ ...labelStyle, marginBottom: '0.75rem' }}>
                                Aktuelle Unterschrift:
                            </label>
                            {signatureExists && currentSignatureUrl ? (
                                <div style={{
                                    background: 'var(--bg-card, #ffffff)',
                                    padding: '0.75rem',
                                    borderRadius: '8px',
                                    border: '1px solid var(--border-light, #e2e8f0)'
                                }}>
                                    <img
                                        src={currentSignatureUrl}
                                        alt="Aktuelle Unterschrift"
                                        style={{
                                            maxWidth: '100%',
                                            height: 'auto',
                                            display: 'block'
                                        }}
                                    />
                                </div>
                            ) : (
                                <p style={{
                                    margin: 0,
                                    color: 'var(--text-secondary, #64748b)',
                                    fontSize: '0.875rem'
                                }}>
                                    Keine Unterschrift vorhanden.
                                </p>
                            )}
                        </div>
                    </>
                )}
            </Modal.Body>

            <Modal.Footer style={{
                borderTop: '1px solid var(--border-light, #e2e8f0)',
                padding: '1rem 1.5rem',
                gap: '0.75rem'
            }}>
                <Button
                    variant="outline-secondary"
                    onClick={handleClose}
                    disabled={isSaving}
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        border: '1px solid var(--border-medium, #cbd5e1)',
                        color: 'var(--text-primary, #2c3e50)',
                        background: 'var(--bg-card, #ffffff)'
                    }}
                >
                    <X size={18} />
                    Schließen
                </Button>
                <Button
                    variant="primary"
                    onClick={handleSave}
                    disabled={isSaving || isLoading}
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        background: 'var(--accent-slate, #5b7c8d)',
                        border: 'none'
                    }}
                >
                    {isSaving ? (
                        <>
                            <span className="spinner-border spinner-border-sm" role="status"></span>
                            Speichern...
                        </>
                    ) : (
                        <>
                            <Save size={18} />
                            Unterschrift speichern
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default SignatureModal;
